/*
 * App Web Demo
 * Author:Manikandabose Nagarajan
 * Created on:20-02-2022
 * Updated on: 05-03-2022
 */

import {
  Button,
  Icon,
  Input,
  Loader,
  Notification,
  Table,
  Tooltip
} from '@scuf/common';
import React, { useEffect, useState } from 'react';
import authService from '../../../authService';
import './SubscribedServicesView.scss';
import i18next from '../../../i18next';
import { AlertModal } from '../PartnerManagement/AlertModal';
import { ReactComponent as UnLinkIcon } from '../../../assets/UnLink.svg';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

interface ISubscribedServicesView {
  orgId: string;
  siteId: string;
  name: string;
  flags?:any;
}

interface IServiceType {
  id: string;
  bundle: {
    id: string;
    name: string;
    desc: string;
    isActive: boolean;
  };
  subscriptionNumber: string;
  orderNumber: string;
  createdOn: string;
  activatedOn: string;
  validTo: string;
  validityDate: string;
}

export const SubscribedServicesView = (props: ISubscribedServicesView) => {
  const [services, setServices] = useState<Array<IServiceType>>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [serviceLoader, setServiceLoader] = useState<boolean>(false);
  const [successNotification, setSuccessNotification] = useState<boolean>(
    false
  );
  const [subscriptionNumber, setSubscriptionNumber] = useState<string>('');
  const [orderNumber, setOrderNumber] = useState<string>('');
  const [successMessage, setSuccessMessgae] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>();
  const [showUnlinkModal, setShowUnlinkModal] = useState<boolean>(false);
  const [selectedServiceName, setSelectedServiceName] = useState<string>('');

  useEffect(() => {
    getServices();
  }, []);
  const getServices = async () => {
    setServiceLoader(true);
    const getAccountSettings = authService
      .authFetch(
        `${authService.BASE_URL}/api/v1/subscriptions/GetSubscriptionsForOnBoarding/orgs/${props.orgId}/nodes/${props.siteId}`,
        { method: 'GET' }
      )
      .then(response => {
        if (response.ok) {
          response.json().then(data => {
            setServices(data.subscriptionDetails);
          });
        }
      })
      .catch(error => {
        console.error('Error:', error);
      });
    setServiceLoader(false);
  };

  const addService = async () => {
    setLoader(true);
    const addingService = authService.authFetch(
      `${authService.BASE_URL}/api/v2/subscriptions/orgs/${props.orgId}/nodes/${props.siteId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscriptionNumber: subscriptionNumber,
          orderNumber: orderNumber })}
    );
    const response = await addingService;
    setLoader(false);
    if(response.ok) {
      setSubscriptionNumber('');
      setOrderNumber('');
      getServices();
      const res = await response.json();
      switch (res.code) {
        case 'S0001':
          setSuccessMessgae(i18next.t('subsactivateSuccess'));
          break;
        case 'S0002':
          setSuccessMessgae(i18next.t('subsactivateFailure'));
          break;
        default:
          setSuccessMessgae(i18next.t('subsactivateSuccess'));
          break;
      }
      setSuccessNotification(true);
    } else {
      const res = await response.json();
      if (res) {
        switch (res.code) {
          case 'E60001':
            setErrorMessage(i18next.t('invalidInputError'));
            break;
          case 'E60002':
            setErrorMessage(i18next.t('subsactivateError'));
            break;
          case 'E60003':
            setErrorMessage(i18next.t('subsnotfoundError'));
            break;
          case 'E60004':
            setErrorMessage(i18next.t('invalidorderError'));
            break;
          case 'E60005':
            setErrorMessage(i18next.t('bundnotfoundError'));
            break;
          case 'E60006':
            const msg = res.message.split('Found');
            setErrorMessage(i18next.t('bundnotfoundError') + msg[1]);
            break;
          case 'E60007':
            setErrorMessage(i18next.t('subsactivateDiffSite'));
            break;
          default:
            setErrorMessage(i18next.t('unknownError'));
            break;
        }
      } else {
        setErrorMessage(i18next.t('unknownError'));
      }
    }
  };

  const onUnlinkClick = (name: string) => {
    setSelectedServiceName(name);
    setShowUnlinkModal(true);
  }
  return (
    <div className="subscribed-services">
      {props.flags.partnerportalsubscription === false && ( 
        <div className="subscribed-section">
          <p className="subscribed-form-title">{i18next.t('subscriptionNumber')}</p>
          <Input
            className="subscribed-input"
            value={subscriptionNumber}
            onChange={(value: string) => {setErrorMessage('');setSubscriptionNumber(value);}}
          />
          <p className="subscribed-form-title">
            {i18next.t('orderNumber')}
          </p>
          <Input
            className="subscribed-input"
            onChange={(value: string) => {setErrorMessage('');setOrderNumber(value);}}
            value={orderNumber}
          />
          {errorMessage && <p className="error-msg">{errorMessage}</p>}
          <div className={ 'submit-subscription' + ( errorMessage &&  ' mt-10' )}>
            <Button
              type="primary"
              className="submit-btn"
              size="medium"
              onClick={addService}
              disabled={!(subscriptionNumber.trim() && orderNumber.trim()) && !errorMessage}
              loading={loader}
              content={i18next.t('addServicesText')}
            />
          </div>
        </div> 
      )} 
      <div className="services-section">
        <Table className="services-list-table">
          <Table.Header>
            <Table.HeaderCell className="table-header" content="Services" />
            <Table.HeaderCell
              className="table-header"
              content={i18next.t('validityText')}
            />
            <Table.HeaderCell
              className="table-header"
              content={i18next.t('action')}
            />
          </Table.Header>
          <Table.Body className="service-table-body">
            {!serviceLoader &&
              services.map((service: IServiceType) => {
                return (
                  <Table.Row>
                    <Table.Cell className="table-body-cell">
                      <div className="service-details">
                        <p className="service-title">{service.bundle.name}</p>
                        <Tooltip
                          element={
                            <Icon
                              className="title-tooltip-icon"
                              root="global"
                              name="badge-info"
                              size="medium"
                            />
                          }
                          content={service.bundle.desc}
                          size="mini"
                          position="top right"
                        />
                      </div>
                    </Table.Cell>
                    <Table.Cell className="table-body-cell date-cell">
                      {service.validityDate.toLowerCase().includes('expire') ? (
                        <p className="expires-text">{service.validityDate}</p>
                      ) : (
                        <p className="services-description">
                          {service.validityDate}
                        </p>
                      )}
                    </Table.Cell>
                    <Table.Cell className="table-body-cell">
                    <div >
                      {!service.validityDate.toLowerCase().includes('expire') ? (

                        <UnLinkIcon className="unlink-icon" onClick={() => onUnlinkClick(service.bundle.name)}/>
                        
                      ) :'-'}
                    </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            {serviceLoader && <Loader className="services-loader"></Loader>}
            {showUnlinkModal &&
                          <AlertModal
                            onClose={() => setShowUnlinkModal(false)}
                            message={i18next.t('unlinkMessage', {
                              siteName: props.name,
                              sku: selectedServiceName
                            })}
                          />
                        }
          </Table.Body>
        </Table>
      </div>
      {successNotification && (
        <Notification
          hasIcon={true}
          className="service-notification"
          onCloseClick={() => setSuccessNotification(false)}
          severity="success"
        >
          {successMessage}
        </Notification>
      )}
    </div>
  );
};

export default withLDConsumer()(SubscribedServicesView);